import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../api';

const AuthContext = createContext(null);

const DEFAULT_PLAN_STATUS = {
  checked: false,
  expired: false,
  trialDaysLeft: null,
  plan: 'Trial',
  expiry: null,
  hasInstagramAccess: false,
  allowedModules: ['*'],
  isModuleAllowed: () => true,
};

const getInitialUser = () => {
  try {
    const item = localStorage.getItem('user');
    return item ? JSON.parse(item) : null;
  } catch {
    return null;
  }
};

const getInitialToken = () => {
  try {
    return localStorage.getItem('token') || null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getInitialUser);
  const [token, setToken] = useState(getInitialToken);
  const [planStatus, setPlanStatus] = useState(DEFAULT_PLAN_STATUS);

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      if (!isMounted.current) return;
      setUser(null);
      setToken(null);
      setPlanStatus(DEFAULT_PLAN_STATUS);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  const login = useCallback((tkn, userData) => {
    try {
      localStorage.setItem('token', tkn);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch {
      // Ignore storage write issues in private mode
    }
    setUser(userData);
    setToken(tkn);
  }, []);

  const logout = useCallback(async () => {
    try {
      // Increments tokenVersion on server — instantly invalidates this token
      await api.post('/auth/logout');
    } catch {
      // Ignore network errors on logout
    }

    try {
      localStorage.clear();
    } catch {
      // Ignore storage clear issues
    }

    setUser(null);
    setToken(null);
    setPlanStatus(DEFAULT_PLAN_STATUS);
  }, []);

  const refreshPlanStatus = useCallback(async () => {
    let currentToken = null;
    try {
      currentToken = localStorage.getItem('token');
    } catch {
      currentToken = null;
    }

    if (!currentToken) return;

    try {
      const { data } = await api.get('/auth/profile');
      if (!isMounted.current) return;

      const plan = data.settings?.plan || 'Trial';
      const rawExpiry = data.settings?.planExpiry;
      const parsedExpiry = rawExpiry ? new Date(rawExpiry) : null;
      const validExpiry = parsedExpiry && !isNaN(parsedExpiry.getTime()) ? parsedExpiry : null;

      const now = new Date();
      const expired = validExpiry ? now > validExpiry : (plan === 'Trial');
      const trialDaysLeft = (plan === 'Trial' && validExpiry)
        ? Math.max(0, Math.floor((validExpiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
        : (plan === 'Trial' ? 0 : null);

      const rawAllowedModules = Array.isArray(data.allowedModules) && data.allowedModules.length > 0
        ? data.allowedModules
        : ['*'];

      const isModuleAllowed = (modKey) => {
        if (!modKey) return true;
        if (data.role === 'superadmin') return true;
        if (!rawAllowedModules || rawAllowedModules.includes('*') || rawAllowedModules.includes('all')) return true;
        return rawAllowedModules.includes(modKey);
      };

      const planLower = (plan || '').toLowerCase();
      const hasInstagramAccess = (
        data.role === 'superadmin' ||
        isModuleAllowed('instagram') ||
        planLower.includes('instagram') ||
        planLower.includes('combo') ||
        planLower.includes('omnichannel') ||
        planLower.includes('ultimate')
      );

      setPlanStatus({
        checked: true,
        expired,
        trialDaysLeft,
        plan,
        expiry: validExpiry,
        hasInstagramAccess,
        allowedModules: rawAllowedModules,
        isModuleAllowed,
      });
    } catch (err) {
      // Silently ignore — interceptor handles forced logout for real auth failures
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        console.warn('[refreshPlanStatus]', err.message);
      }
    }
  }, []);

  useEffect(() => {
    if (token) {
      refreshPlanStatus();
    }
  }, [token, refreshPlanStatus]);

  const contextValue = useMemo(
    () => ({ user, token, login, logout, planStatus, refreshPlanStatus }),
    [user, token, login, logout, planStatus, refreshPlanStatus]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
